import * as React from 'react'

import { Form as FactorialForm } from 'factorial-form'
import { computed, makeObservable } from 'mobx'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import TeamPicker from 'modules/reports/components/TeamPicker'
import { VARIABLE_TYPES } from 'modules/reports/store/models/Dashboard'

const DEFAULT_VALUE_KEY = 'default-value'

export class VariableForm extends FactorialForm {
  constructor(values: {}) {
    super(values, {
      name: 'string',
      type: 'string',
      default_value: 'string',
    })
    makeObservable(this, {
      isCompleted: computed,
    })
  }

  get isCompleted(): boolean {
    return !!this.get('name').value?.length
  }
}

const DefaultValue = observer(({ form }: { form: VariableForm }) => {
  if (!form.get('type').value) {
    return null
  }

  switch (form.get('type').value) {
    case VARIABLE_TYPES.date:
      return (
        <FormRow>
          <DayPicker
            key={DEFAULT_VALUE_KEY}
            label={i18n.t('custom_reports.default_value')}
            field={form.get('default_value')}
            valueFormat="backend"
          />
        </FormRow>
      )

    case VARIABLE_TYPES.team:
      return (
        <FormRow>
          <TeamPicker
            key={DEFAULT_VALUE_KEY}
            label={i18n.t('custom_reports.default_value')}
            field={form.get('default_value')}
          />
        </FormRow>
      )

    case VARIABLE_TYPES.text:
    default:
      return (
        <FormRow>
          <Text
            key={DEFAULT_VALUE_KEY}
            label={i18n.t('custom_reports.default_value')}
            field={form.get('default_value')}
          />
        </FormRow>
      )
  }
})

const validateNonRepeated = (value: string, variables: any[]) => {
  const variableValues = variables.map((variable) => variable.name)

  if (variableValues.includes(value)) {
    return i18n.t('custom_reports.repeated_variable_error')
  }
}

interface Props {
  form: VariableForm
  variables: any[]
  readOnly: boolean
}

const FormComponent = ({ form, variables, readOnly = false }: Props) => {
  const options = [
    {
      label: i18n.t('custom_reports.variables.date'),
      value: VARIABLE_TYPES.date,
    },
    {
      label: i18n.t('custom_reports.variables.text'),
      value: VARIABLE_TYPES.text,
    },
    {
      label: i18n.t('custom_reports.variables.team'),
      value: VARIABLE_TYPES.team,
    },
  ]

  return (
    <>
      <FormRow>
        <Text
          label={i18n.t('common.name')}
          field={form.get('name')}
          readonly={readOnly}
          onChange={(field) => {
            const regex = /[^a-zA-Z\-_]/g
            field.set(field.value.replace(regex, ''))
          }}
          validation={(value) => validateNonRepeated(value, variables)}
        />
      </FormRow>
      <FormRow>
        <Select
          none={i18n.t('common.none')}
          label={i18n.t('common.type')}
          field={form.get('type')}
          options={options}
          showOriginalLabelText
          readonly={readOnly}
        />
      </FormRow>
      <DefaultValue form={form} />
    </>
  )
}

export default observer(FormComponent)
